import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { validateEmail, ERR_INPUT_CLS, ERR_MSG_CLS } from "@/lib/formValidation";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * High-conversion lead-capture CTA placed at the bottom of each disaster
 * guide. Storm-damage searches convert at 8-15% to quote requests (vs. 1-3%
 * for general info), so this captures intent-rich traffic into the existing
 * /api/leads pipeline.
 *
 * Each placement passes `source` and `projectType` so admin analytics can
 * segment lead volume by guide slug + project type later.
 *
 * Props:
 *   - headline       (override default)
 *   - subtext        (override default)
 *   - projectType    (e.g., "Roof Replacement", "Wildfire Rebuild") — stored on lead
 *   - source         (defaults to "damage-guide-bottom" — slug-specific override)
 *   - ctaLabel       (override the button label)
 *   - urgencyNote    (small disclaimer line under the form)
 */
export default function DisasterQuoteCTA({
  headline = "Get 3 free contractor quotes for your storm-damaged roof",
  subtext = "Vetted local roofers who handle insurance claims. No spam, no obligation, no sales calls — we just send the quotes.",
  projectType = "Storm Damage Roof Replacement",
  source = "damage-guide-bottom",
  ctaLabel = "Request my 3 free quotes",
  urgencyNote = "Most homeowners receive 3 quotes within 48 hours. Free service — funded by contractor referral fees, not by you.",
}) {
  const [form, setForm] = useState({ name: "", email: "", zip_code: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const eMap = {};
    if (!form.name.trim()) eMap.name = "Please enter your name.";
    const emailErr = validateEmail(form.email, { requiredMsg: "Enter your email so contractors can send you quotes." });
    if (emailErr) eMap.email = emailErr;
    if (Object.keys(eMap).length) {
      setErrors(eMap);
      track("optin_submit_attempted_with_errors", { form: "disaster-quote-cta", source, missing: Object.keys(eMap) });
      const first = Object.keys(eMap)[0];
      document.querySelector(`[data-testid="disaster-quote-cta-${first}"]`)?.focus();
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        zip_code: form.zip_code.trim() || undefined,
        project_type: projectType,
        source,
        notes: `Disaster guide CTA — ${source}`,
      });
      setSubmitted(true);
      toast.success("Got it — we'll match you with 3 local roofers within 48 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="my-10 rounded-2xl border-2 border-emerald-600 bg-emerald-50 px-6 py-7 text-center"
        data-testid="disaster-quote-cta-success"
      >
        <p className="font-display text-xl font-semibold text-emerald-900">
          ✓ Thanks — your quote request is in.
        </p>
        <p className="mt-2 text-sm text-emerald-800">
          We'll match you with 3 vetted local roofers within 48 hours. Check your inbox
          (including spam) for the first response.
        </p>
        <p className="mt-3 text-xs text-emerald-700">
          While you wait, read our{" "}
          <a href="/guides/how-to-file-roof-insurance-claim-step-by-step-2026" className="underline">
            step-by-step insurance claim guide
          </a>{" "}
          so you're ready when the roofers call.
        </p>
      </div>
    );
  }

  return (
    <div
      className="my-10 rounded-2xl border-2 border-emerald-600 bg-gradient-to-br from-emerald-50 to-white px-6 py-7"
      data-testid="disaster-quote-cta"
    >
      <p className="font-display text-xl font-semibold text-slate-900">{headline}</p>
      <p className="mt-2 text-sm leading-relaxed text-slate-700">{subtext}</p>
      <form onSubmit={submit} noValidate className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_120px_auto]">
        <div>
          <input
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); if (errors.name) setErrors((p) => ({ ...p, name: undefined })); }}
            aria-invalid={!!errors.name}
            className={`w-full rounded-lg bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${errors.name ? "border border-red-500 focus:border-red-500 focus:ring-red-200" : "border border-slate-300 focus:border-emerald-600 focus:ring-emerald-200"}`}
            data-testid="disaster-quote-cta-name"
          />
          {errors.name && (<p className={`mt-1 ${ERR_MSG_CLS}`} data-testid="disaster-quote-cta-name-error">{errors.name}</p>)}
        </div>
        <div>
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
            aria-invalid={!!errors.email}
            className={`w-full rounded-lg bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${errors.email ? "border border-red-500 focus:border-red-500 focus:ring-red-200" : "border border-slate-300 focus:border-emerald-600 focus:ring-emerald-200"}`}
            data-testid="disaster-quote-cta-email"
          />
          {errors.email && (<p className={`mt-1 ${ERR_MSG_CLS}`} data-testid="disaster-quote-cta-email-error">{errors.email}</p>)}
        </div>
        <input
          type="text"
          inputMode="numeric"
          placeholder="ZIP code"
          maxLength={10}
          value={form.zip_code}
          onChange={(e) => setForm((f) => ({ ...f, zip_code: e.target.value }))}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
          data-testid="disaster-quote-cta-zip"
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-lg bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:opacity-60"
          data-testid="disaster-quote-cta-submit"
        >
          {busy ? "Sending…" : ctaLabel}
        </button>
      </form>
      <p className="mt-3 text-xs text-slate-500">{urgencyNote}</p>
    </div>
  );
}
